import type {
  AllMerchantFields,
  BaseMerchantFieldMapper,
  BusinessType,
  MerchantAccount,
  MerchantFieldSetValueMap,
} from "client/lib/apt-merchant";
import { FieldSets, Merchant } from "client/lib/apt-merchant";
import { merchantAccountInitialState } from "client/store/constants";
import { RestoreResponse, RestoreResponseField } from "client/types";

import {
  BusinessOwnerValueMap,
  StoreLocationValueMap,
} from "../lib/apt-merchant/src/fieldsets";

const safeGetRestoredValue = (
  ref: RestoreResponseField | undefined,
  defaultValue = ""
) => {
  /**
   * adding support for old data structure as well as new from payload
   *
   * old = { value: "123", errorMsg: ""}
   * new = "123"
   */
  if (typeof ref === "string") {
    return ref;
  }
  return ref?.value ?? defaultValue;
};

const transformOwnerResponse = (
  owner: FieldSets.BusinessOwnerValueMapper<RestoreResponseField>
): FieldSets.BusinessOwnerValueMap => ({
  city: safeGetRestoredValue(owner?.city),
  dob: safeGetRestoredValue(owner?.dob),
  state: safeGetRestoredValue(owner?.state),
  postalCode: safeGetRestoredValue(owner?.postalCode),
  firstName: safeGetRestoredValue(owner?.firstName),
  lastName: safeGetRestoredValue(owner?.lastName),
  ssn: safeGetRestoredValue(owner?.ssn),
  street: safeGetRestoredValue(owner?.street),
  street2: safeGetRestoredValue(owner?.street2),
  govId: safeGetRestoredValue(owner?.govId),
  govIdBackside: safeGetRestoredValue(owner?.govIdBackside),
  certifiedGovIdPrimary: safeGetRestoredValue(owner?.certifiedGovIdPrimary),
  certifiedGovIdSecondary: safeGetRestoredValue(owner?.certifiedGovIdSecondary),
  verificationMethod: safeGetRestoredValue(owner?.verificationMethod),
});

const transformStoreLocationResponseFromRestore = (
  location: FieldSets.StoreLocationValueMapper<RestoreResponseField>
): FieldSets.StoreLocationValueMap => ({
  branchNumber: safeGetRestoredValue(location.branchNumber),
  isMall: safeGetRestoredValue(location.isMall),
  isResidence: safeGetRestoredValue(location.isResidence),
  label: safeGetRestoredValue(location.label),
  locationCity: safeGetRestoredValue(location.locationCity),
  locationName: safeGetRestoredValue(location.locationName),
  locationPostalCode: safeGetRestoredValue(location.locationPostalCode),
  locationState: safeGetRestoredValue(location.locationState),
  locationStreet: safeGetRestoredValue(location.locationStreet),
  locationStreet2: safeGetRestoredValue(location.locationStreet2),
  mallName: safeGetRestoredValue(location.mallName),
});

const transformStoreLocationResponseFromAccount = (
  location: FieldSets.StoreLocationValueMap
): FieldSets.StoreLocationValueMap => ({
  branchNumber: location?.branchNumber ?? "",
  isMall: location?.isMall ?? false,
  isResidence: location?.isResidence ?? false,
  label: location?.label ?? "",
  locationCity: location?.locationCity ?? "",
  locationName: location?.locationName ?? "",
  locationPostalCode: location?.locationPostalCode ?? "",
  locationState: location?.locationState ?? "",
  locationStreet: location?.locationStreet ?? "",
  locationStreet2: location?.locationStreet2 ?? "",
  mallName: location?.mallName ?? "",
});

export const transformAccountValues = (
  response: RestoreResponse
): MerchantAccount => {
  const {
    account,
    bsb,
    bsb2,
    cashPayChannel,
    cashPayOnboardingStatus,
    contactlessPayment,
    customerSupportEmail,
    customerSupportPhone,
    customerSupportWebsite,
    inStoreDirectDebitAuthorized,
    inStoreTrainingComplete,
    merchantId,
    mlsProspectRef,
    onboardingGroup,
    onboardingLink,
    onboardingStatus,
    opportunityOwnerEmail,
    ownerControls,
    registrationNumber,
    requireInsurance,
    requireTrustDeed,
    routing,
    sfProspectId,
    selfFulfillment,
    selfServeInStore,
    totalAnnualSalesInStore,
    trustDeedRequired,
    website,
  } = merchantAccountInitialState;
  return {
    account: response.account?.account ?? account,
    bsb: response.account?.bsb ?? bsb,
    bsb2: response.account?.bsb2 ?? bsb2,
    cashPayChannel: response.account?.cashPayChannel ?? cashPayChannel,
    cashPayOnboardingStatus:
      response.account?.cashPayOnboardingStatus ?? cashPayOnboardingStatus,
    contactlessPayment:
      response.account?.contactlessPayment ?? contactlessPayment,
    customerSupportEmail:
      response.account?.customerSupportEmail ?? customerSupportEmail,
    customerSupportPhone:
      response.account?.customerSupportPhone ?? customerSupportPhone,
    customerSupportWebsite:
      response.account?.customerSupportWebsite ?? customerSupportWebsite,
    inStoreDirectDebitAuthorized:
      response.account?.inStoreDirectDebitAuthorized ??
      inStoreDirectDebitAuthorized,
    inStoreTrainingComplete:
      response.account?.inStoreTrainingComplete ?? inStoreTrainingComplete,
    locations: getStoreLocationsFromResponse(response),
    merchantId: response.account?.merchantId ?? merchantId,
    onboardingGroup: response.account?.onboardingGroup ?? onboardingGroup,
    onboardingLink: response.account?.onboardingLink ?? onboardingLink,
    onboardingStatus: response.account?.onboardingStatus ?? onboardingStatus,
    opportunityOwnerEmail:
      response.account?.opportunityOwnerEmail ?? opportunityOwnerEmail,
    ownerControls: response.account?.ownerControls ?? ownerControls,
    owners: getOwnersFromResponse(response),
    mlsProspectRef: response.account?.mlsProspectRef ?? mlsProspectRef,
    registrationNumber:
      response.account?.registrationNumber ?? registrationNumber,
    requireInsurance: response.account?.requireInsurance ?? requireInsurance,
    requireTrustDeed: response.account?.requireTrustDeed ?? requireTrustDeed,
    routing: response.account?.routing ?? routing,
    sfProspectId: response.account?.sfProspectId ?? sfProspectId,
    selfFulfillment: response.account?.selfFulfillment ?? selfFulfillment,
    selfServeInStore: response.account?.selfServeInStore ?? selfServeInStore,
    totalAnnualSalesInStore:
      response.account?.totalAnnualSalesInStore ?? totalAnnualSalesInStore,
    trustDeedRequired: response.account?.trustDeedRequired ?? trustDeedRequired,
    website: response.account?.website ?? website,
  };
};

// current mop backend does not include any bank info fields in restore response
export type BankInfoFields =
  | "account"
  | "account2"
  | "bankAccountName"
  | "bankAccountName2"
  | "bankBranch"
  | "bankBranch2"
  | "bankName"
  | "bankName2"
  | "bsb"
  | "bsb2"
  | "canadaChequingAccount"
  | "canadaRoutingInstitutionIdNumber"
  | "canadaRoutingTransitIdNumber"
  | "confirmAccount"
  | "confirmAccount2"
  | "confirmCanadaChequingAccount"
  | "directDebitAgreement"
  | "directDebitAgreement2"
  | "email"
  | "email2"
  | "routing"
  | "signatoryName"
  | "signatoryName2"
  | "signatoryType"
  | "signatoryType2"
  | "sortCode"
  | "sortCode2";

export type CustomerSupportFields =
  | "customerSupportPhone"
  | "customerSupportEmail"
  | "customerSupportWebsite";

export type CashPayFields = "cashPayLogoUrl";

export type TransformResponse = Partial<AllMerchantFields>;

export const convertSubIndustryFieldForSave = (field: string): string => {
  const salesForceSubIndustry = field
    .replace("General Practice", "8511 General Practice Medical Services")
    .replace("Dental Services", "8531 Dental Services")
    .replace("Optometry", "8532 Optometry and Optical Dispensing")
    .replace(
      "Pharmaceutical Retailing",
      "4271 Pharmaceutical, Cosmetic and Toiletry Goods Retailing"
    );
  return salesForceSubIndustry;
};

const getPrefilledBankAccountName = (
  businessType: BusinessType,
  firstOwner: FieldSets.BusinessOwnerValueMap,
  storeName: string
): string => {
  // sole prop/trader use owner
  if (
    businessType === "Sole Trader" ||
    businessType === "Sole Proprietorship"
  ) {
    return `${firstOwner.firstName} ${firstOwner.lastName}`;
  }
  // for any other combination, populate bank account from store name
  return storeName;
};

const getStoreLocationsFromResponse = (
  response: RestoreResponse
): StoreLocationValueMap[] => {
  if (response.account?.locations && response.account?.locations.length > 0) {
    return response.account.locations.map((location) =>
      transformStoreLocationResponseFromAccount(location)
    );
  } else if (response.restore?.locations) {
    return response.restore?.locations.map((location) =>
      transformStoreLocationResponseFromRestore(location ?? {})
    );
  } else {
    return [];
  }
};

const getOwnersFromResponse = (
  response: RestoreResponse
): BusinessOwnerValueMap[] => {
  if (response.restore?.owners && response.restore?.owners.length > 0) {
    return response.restore.owners.map((owner) =>
      transformOwnerResponse(owner)
    );
  } else if (response.account?.owners) {
    return response.account?.owners.map((owner) =>
      transformOwnerResponse(owner ?? {})
    );
  } else {
    return [];
  }
};

type TransformConfig = {
  fieldProperty: keyof TransformResponse;
  defaultValue?: string | boolean | number;
  parser?: (val: any) => any;
};

export const determineValueFromRestorePayload = (
  response: RestoreResponse,
  field: TransformConfig["fieldProperty"],
  parser: TransformConfig["parser"] | null,
  defaultValue: TransformConfig["defaultValue"] = undefined
): any => {
  // this is meant to take any field property in MOP and check both the account and restore properties from the api/restore response for presence of a value. it could be on either or
  let valueOnAccount =
    response?.account?.[field as keyof MerchantAccount] ?? undefined;
  if (typeof valueOnAccount === "boolean" && Boolean(valueOnAccount)) {
    // for boolean true values, in order to hydrate the input, we need to coerce to string "Yes". We skip false booleans because many default to that
    valueOnAccount = "Yes";
  }

  const restoredVal =
    response?.restore?.[
      field as keyof BaseMerchantFieldMapper<RestoreResponseField>
    ];

  const valueOnRestore = safeGetRestoredValue(restoredVal);

  const determinedValue = Boolean(valueOnRestore)
    ? valueOnRestore
    : Boolean(valueOnAccount)
    ? valueOnAccount
    : defaultValue;
  if (!determinedValue) {
    // skip parsing if null or undefined
    return determinedValue;
  }
  return parser ? parser(determinedValue) : determinedValue;
};

// list of fields we expect on either restore or account
const transformFields: TransformConfig[] = [
  { fieldProperty: "account" },
  { fieldProperty: "account2" },
  { fieldProperty: "abn" },
  { fieldProperty: "acquirer", defaultValue: "" },
  { fieldProperty: "artisan", defaultValue: "" },
  { fieldProperty: "aveTimeToShip", defaultValue: "" },
  { fieldProperty: "bankName", defaultValue: "" },
  { fieldProperty: "bankName2", defaultValue: "" },
  { fieldProperty: "bankBranch", defaultValue: "" },
  { fieldProperty: "bankBranch2", defaultValue: "" },
  { fieldProperty: "bsb", defaultValue: "" },
  { fieldProperty: "bsb2", defaultValue: "" },
  { fieldProperty: "businessCity", defaultValue: "" },
  { fieldProperty: "businessPostalCode", defaultValue: "" },
  { fieldProperty: "businessState", defaultValue: "" },
  { fieldProperty: "businessStreet", defaultValue: "" },
  { fieldProperty: "businessStreet2", defaultValue: "" },
  { fieldProperty: "businessType", defaultValue: "" },
  {
    fieldProperty: "canadaChequingAccount",
    defaultValue: "",
    parser: (val) => parseInt(val, 10),
  },
  { fieldProperty: "canadaRoutingTransitIdNumber" },
  { fieldProperty: "canadaRoutingInstitutionIdNumber", defaultValue: "" },
  { fieldProperty: "companyNumber" },
  {
    fieldProperty: "confirmCanadaChequingAccount",
    defaultValue: "",
    parser: (val) => parseInt(val, 10),
  },
  { fieldProperty: "confirmAccount" },
  { fieldProperty: "confirmAccount2" },
  { fieldProperty: "contactlessPayment", defaultValue: "" },
  { fieldProperty: "craBusinessNumber", defaultValue: "" },
  { fieldProperty: "craRegistryId", defaultValue: "" },
  { fieldProperty: "currenciesAvailable", defaultValue: "" },
  { fieldProperty: "ddsRestricted", defaultValue: "" },
  { fieldProperty: "customerSupportEmail", defaultValue: "" },
  { fieldProperty: "customerSupportPhone" },
  { fieldProperty: "customerSupportWebsite", defaultValue: "" },
  { fieldProperty: "email", defaultValue: "" },
  { fieldProperty: "email2", defaultValue: "" },
  { fieldProperty: "ein", defaultValue: "" },
  { fieldProperty: "facebookUrl", defaultValue: "" },
  { fieldProperty: "hasConstitution", defaultValue: "" },
  { fieldProperty: "hasShareholderAgreement", defaultValue: "" },
  { fieldProperty: "industry", defaultValue: "" },
  { fieldProperty: "interestedInXBT", defaultValue: "" },
  { fieldProperty: "legalBusinessName", defaultValue: "" },
  { fieldProperty: "multiTerminal", defaultValue: "" },
  { fieldProperty: "nonFamilyEmployee", defaultValue: "" },
  {
    fieldProperty: "numOfStores",
    parser: (val) => parseInt(val, 10),
  },
  { fieldProperty: "partnershipAgreement" },
  { fieldProperty: "phone", defaultValue: "" },
  { fieldProperty: "platform", defaultValue: "" },
  { fieldProperty: "preOrders", defaultValue: "" },
  { fieldProperty: "proofOfInsurance" },
  { fieldProperty: "registeredCountry", defaultValue: "" },
  { fieldProperty: "registrationNumber", defaultValue: "" },
  { fieldProperty: "reseller", defaultValue: "" },
  { fieldProperty: "routing" },
  { fieldProperty: "routing2" },
  { fieldProperty: "salesChannel", defaultValue: "" },
  { fieldProperty: "selfFulfillment", defaultValue: "" },
  { fieldProperty: "selfServeInStore", defaultValue: "" },
  { fieldProperty: "signatoryName", defaultValue: "" },
  { fieldProperty: "signatoryName2", defaultValue: "" },
  { fieldProperty: "sortCode", defaultValue: "" },
  { fieldProperty: "sortCode2", defaultValue: "" },
  { fieldProperty: "storeName", defaultValue: "" },
  { fieldProperty: "stockMarketLister", defaultValue: "" },
  { fieldProperty: "subIndustry", defaultValue: "" },
  { fieldProperty: "totalAnnualSales", defaultValue: "" },
  {
    fieldProperty: "totalAnnualSalesInStore",
    defaultValue: "",
    parser: (val) => parseInt(val, 10),
  },
  { fieldProperty: "trustDeed" },
  { fieldProperty: "website", defaultValue: "" },
  { fieldProperty: "websiteNotLive", defaultValue: "" },
];

export const transformResponse = (
  response: RestoreResponse
): TransformResponse => {
  const storeName = determineValueFromRestorePayload(
    response,
    "storeName",
    null,
    ""
  );
  const businessType = determineValueFromRestorePayload(
    response,
    "businessType",
    null,
    ""
  ) as BusinessType;
  const owners = getOwnersFromResponse(response);
  const firstOwner = transformOwnerResponse(owners?.[0]);
  const output: TransformResponse = {
    bankAccountName: getPrefilledBankAccountName(
      businessType,
      firstOwner,
      storeName
    ),
    bankAccountName2: getPrefilledBankAccountName(
      businessType,
      firstOwner,
      storeName
    ),
    locations: getStoreLocationsFromResponse(response),
    owners,
  };

  transformFields.forEach(({ fieldProperty, parser, defaultValue }) => {
    output[fieldProperty] = determineValueFromRestorePayload(
      response,
      fieldProperty,
      parser ?? null,
      defaultValue
    );
  });
  return output;
};

type MerchantFieldsFromJson = TransformResponse &
  {
    [key in BankInfoFields]?: any;
  };

export const createMerchantFromMerchantFieldMap = ({
  companyInformation,
  customerSupport,
  lead,
  locations,
  owners,
  primaryBank,
  secondaryBank,
  storeDetails,
}: MerchantFieldSetValueMap): Merchant => {
  const merchant = Merchant.getInstance();
  merchant.companyInformation = FieldSets.CompanyInformationFieldSet.fromJson(
    merchant.companyInformation,
    companyInformation
  );
  merchant.customerSupport = FieldSets.CustomerSupportFieldSet.fromJson(
    merchant.customerSupport,
    customerSupport
  );
  merchant.lead = FieldSets.LeadFieldSet.fromJson(merchant.lead, lead);
  merchant.locations = (locations ?? []).map((location, index) =>
    FieldSets.StoreLocation.fromJson(
      new FieldSets.StoreLocation(merchant, {
        locationIndex: index,
      }),
      location
    )
  );
  merchant.owners = (owners ?? []).map((owner, index) =>
    FieldSets.BusinessOwner.fromJson(
      new FieldSets.BusinessOwner(merchant, {
        controlValues: {
          requireId:
            merchant.merchantAccount.ownerControls[index]?.requireId ?? false,
          requireCertifiedId:
            merchant.merchantAccount.ownerControls[index]?.requireCertifiedId ??
            false,
        },
        ownerIndex: index,
      }),
      owner
    )
  );
  merchant.primaryBank = FieldSets.BankFieldSet.fromJson(
    merchant.primaryBank,
    primaryBank
  );
  merchant.secondaryBank = FieldSets.BankFieldSet.fromJson(
    merchant.secondaryBank,
    secondaryBank
  );
  merchant.storeDetails = FieldSets.StoreDetailsFieldSet.fromJson(
    merchant.storeDetails,
    storeDetails
  );

  return merchant;
};

export const createMerchantFromJson = ({
  abn,
  account,
  account2,
  acquirer,
  artisan,
  aveTimeToShip,
  bankAccountName,
  bankAccountName2,
  bankBranch,
  bankBranch2,
  bankName,
  bankName2,
  bsb,
  bsb2,
  businessCity,
  businessPostalCode,
  businessState,
  businessStreet,
  businessStreet2,
  businessType,
  canadaChequingAccount,
  canadaRoutingInstitutionIdNumber,
  canadaRoutingTransitIdNumber,
  companyNumber,
  confirmAccount,
  confirmAccount2,
  confirmCanadaChequingAccount,
  contactlessPayment,
  craBusinessNumber,
  craRegistryId,
  currenciesAvailable,
  customerSupportEmail,
  customerSupportPhone,
  customerSupportWebsite,
  ddsRestricted,
  directDebitAgreement,
  directDebitAgreement2,
  ein,
  email,
  email2,
  facebookUrl,
  hasConstitution,
  hasShareholderAgreement,
  industry,
  interestedInXBT,
  legalBusinessName,
  locations,
  multiTerminal,
  nonFamilyEmployee,
  numOfStores,
  owners,
  partnershipAgreement,
  phone,
  platform,
  preOrders,
  proofOfInsurance,
  registeredCountry,
  registrationNumber,
  reseller,
  routing,
  routing2,
  salesChannel,
  selfFulfillment,
  selfServeInStore,
  signatoryName,
  signatoryName2,
  signatoryType,
  signatoryType2,
  sortCode,
  sortCode2,
  stockMarketLister,
  storeName,
  subIndustry,
  totalAnnualSales,
  totalAnnualSalesInStore,
  trustDeed,
  website,
  websiteNotLive,
}: MerchantFieldsFromJson): Merchant => {
  const merchant = Merchant.getInstance();
  merchant.companyInformation = FieldSets.CompanyInformationFieldSet.fromJson(
    merchant.companyInformation,
    {
      abn,
      businessCity,
      businessPostalCode,
      businessState,
      businessStreet,
      businessStreet2,
      businessType,
      companyNumber,
      craBusinessNumber,
      craRegistryId,
      ein,
      hasConstitution,
      hasShareholderAgreement,
      legalBusinessName,
      partnershipAgreement,
      proofOfInsurance,
      registeredCountry,
      registrationNumber,
      stockMarketLister,
      trustDeed,
    }
  );

  merchant.customerSupport = FieldSets.CustomerSupportFieldSet.fromJson(
    merchant.customerSupport,
    {
      customerSupportEmail,
      customerSupportPhone,
      customerSupportWebsite,
    }
  );

  merchant.lead = FieldSets.LeadFieldSet.fromJson(merchant.lead, {
    acquirer,
    contactlessPayment,
    firstName: owners?.[0]?.firstName ?? "",
    industry,
    lastName: owners?.[0]?.lastName ?? "",
    numOfStores,
    phone,
    platform,
    registeredCountry,
    salesChannel,
    storeName,
    subIndustry,
    totalAnnualSales,
    totalAnnualSalesInStore,
    website,
  });

  merchant.locations = (locations ?? []).map((location, index) =>
    FieldSets.StoreLocation.fromJson(
      new FieldSets.StoreLocation(merchant, { locationIndex: index }),
      location
    )
  );

  merchant.owners = (owners ?? []).map((owner, index) =>
    FieldSets.BusinessOwner.fromJson(
      new FieldSets.BusinessOwner(merchant, {
        controlValues: {
          requireId:
            merchant.merchantAccount.ownerControls[index]?.requireId ?? false,
          requireCertifiedId:
            merchant.merchantAccount.ownerControls[index]?.requireCertifiedId ??
            false,
        },
        ownerIndex: index,
      }),
      owner
    )
  );

  merchant.primaryBank = FieldSets.BankFieldSet.fromJson(merchant.primaryBank, {
    account,
    bankAccountName,
    bankBranch,
    bankName,
    bsb,
    canadaChequingAccount,
    canadaRoutingInstitutionIdNumber,
    canadaRoutingTransitIdNumber,
    confirmAccount,
    confirmCanadaChequingAccount,
    directDebitAgreement,
    email,
    routing,
    signatoryName,
    signatoryType,
    sortCode,
  });

  merchant.secondaryBank = FieldSets.BankFieldSet.fromJson(
    merchant.secondaryBank,
    {
      account2,
      bankAccountName2,
      bankBranch2,
      bankName2,
      bsb2,
      confirmAccount2,
      directDebitAgreement2,
      email2,
      routing2,
      signatoryName2,
      signatoryType2,
      sortCode2,
    }
  );

  merchant.storeDetails = FieldSets.StoreDetailsFieldSet.fromJson(
    merchant.storeDetails,
    {
      acquirer,
      artisan,
      aveTimeToShip,
      contactlessPayment,
      currenciesAvailable,
      ddsRestricted,
      facebookUrl,
      industry,
      interestedInXBT,
      multiTerminal,
      nonFamilyEmployee,
      numOfStores,
      platform,
      preOrders,
      reseller,
      salesChannel,
      selfFulfillment,
      selfServeInStore,
      storeName,
      subIndustry,
      totalAnnualSales,
      totalAnnualSalesInStore,
      website,
      websiteNotLive,
    }
  );

  return merchant;
};

export const transformFromFixture = (rawJson: any): Merchant => {
  const transformedJson = transformResponse(rawJson);
  return createMerchantFromJson(transformedJson);
};
